/**
 * Form builder review helpers (F9): plain-English show/hide sentences, the
 * "what's changed since the live version" list, and the pre-publish checks.
 * Pure functions, ported from the Claude Design handoff
 * (`design/handoff-form-builder/builder-data.js`).
 */

import type { ConditionalVisibility, FieldDef, FieldType, VisibilityRule } from './fields.js';
import {
  ADDRESS_COMPONENT_LABELS,
  FIELD_TYPE_CATALOGUE,
  isVisibilityGroup,
  resolveStaticOptions,
  type AddressComponent,
} from './fields.js';
import type { FormSettings, IqMappingEntry } from './forms.js';
import { looksLikeIqField, suggestContactField } from './iq-fields.js';

const CHOICE_TYPES: FieldType[] = ['select', 'radio', 'checkbox', 'multiselect'];

/** The builder's plain name for a field type, e.g. "Short answer". */
export function fieldTypeName(type: FieldType): string {
  return FIELD_TYPE_CATALOGUE.find((t) => t.type === type)?.plainName ?? type;
}

// ---------------------------------------------------------------------------
// Show/hide rules as sentences
// ---------------------------------------------------------------------------

const OP_WORDS: Record<string, string> = {
  eq: 'is',
  in: 'is',
  neq: 'is not',
  contains: 'includes',
  gt: 'is more than',
  lt: 'is less than',
  empty: 'is left blank',
  not_empty: 'has been answered',
};

const COUNTRY_ATTR_WORDS: Record<string, string> = { D_EUCountry: 'is in the EU' };

/** One piece of a rule sentence; `strong` pieces are question names and answers. */
export interface RuleSegment {
  text: string;
  strong: boolean;
}

/** The question (or address part / country attribute) a rule's `fieldId` names. */
export function ruleDriverLabel(fields: FieldDef[], path: string): string | null {
  const [base, part, attr] = path.split('.');
  const f = fields.find((x) => x.id === base);
  if (!f) return null;
  if (!part) return f.label;
  if (attr) return `${f.label}: country ${COUNTRY_ATTR_WORDS[attr] ? `(${COUNTRY_ATTR_WORDS[attr]})` : attr}`;
  const partLabel = ADDRESS_COMPONENT_LABELS[part as AddressComponent] ?? part;
  return `${f.label}: ${partLabel.toLowerCase()}`;
}

/** How an answer reads in a rule: a stored value (e.g. a country code) as its label. */
export type AnswerLabel = (fieldId: string, value: string) => string | undefined;

/** Labels from the driver question's own answers (static, or iQ answers inlined as `choices`). */
function choiceLabel(fields: FieldDef[]): AnswerLabel {
  return (fieldId, value) => {
    const f = fields.find((x) => x.id === fieldId);
    return f ? resolveStaticOptions(f).find((o) => o.value === value)?.label : undefined;
  };
}

function leafSegments(r: VisibilityRule, fields: FieldDef[], labelOf: AnswerLabel): RuleSegment[] {
  const noValue = r.op === 'empty' || r.op === 'not_empty';
  const out: RuleSegment[] = [
    { text: ruleDriverLabel(fields, r.fieldId) ?? 'a deleted question', strong: true },
    { text: ` ${OP_WORDS[r.op] ?? r.op}${noValue ? '' : ' '}`, strong: false },
  ];
  if (noValue) return out;
  const values = (r.op === 'in' ? (r.value ?? '').split(',') : [r.value ?? ''])
    .map((v) => v.trim())
    .filter(Boolean);
  if (!values.length) out.push({ text: '…', strong: true });
  values.forEach((v, i) => {
    if (i) out.push({ text: i === values.length - 1 ? ' or ' : ', ', strong: false });
    out.push({ text: labelOf(r.fieldId, v) ?? v, strong: true });
  });
  return out;
}

/** A show/hide rule as sentence pieces, e.g. "Company type is Limited Company or …". */
export function visibilitySegments(
  vis: ConditionalVisibility,
  fields: FieldDef[],
  labelOf: AnswerLabel = choiceLabel(fields),
): RuleSegment[] {
  const out: RuleSegment[] = [];
  (vis.rules ?? []).forEach((n, i) => {
    if (i) out.push({ text: vis.match === 'any' ? ' or ' : ' and ', strong: false });
    if (isVisibilityGroup(n)) {
      out.push({ text: '(', strong: false }, ...visibilitySegments(n, fields, labelOf), { text: ')', strong: false });
    } else {
      out.push(...leafSegments(n as VisibilityRule, fields, labelOf));
    }
  });
  return out;
}

export function visibilityText(vis: ConditionalVisibility, fields: FieldDef[]): string {
  return visibilitySegments(vis, fields)
    .map((s) => s.text)
    .join('');
}

function hasRules(f: FieldDef): boolean {
  return Boolean(f.visibleWhen && (f.visibleWhen as ConditionalVisibility).rules?.length);
}

function walkRules(vis: ConditionalVisibility | undefined, fn: (r: VisibilityRule) => void): void {
  for (const n of vis?.rules ?? []) {
    if (isVisibilityGroup(n)) walkRules(n, fn);
    else fn(n as VisibilityRule);
  }
}

// ---------------------------------------------------------------------------
// What's changed since the live version
// ---------------------------------------------------------------------------

export interface FieldChange {
  kind: 'add' | 'remove' | 'edit' | 'move';
  /** The question to jump to (absent for removals and reordering). */
  fieldId: string | null;
  text: string;
  detail: string;
}

/** The step (heading) a field sits in. */
function stepNameOf(fields: FieldDef[], id: string): string {
  let step = '';
  for (const f of fields) {
    if (f.type === 'heading') step = f.label;
    if (f.id === id) return step;
  }
  return '';
}

function answersOf(f: FieldDef): string[] {
  return f.choices?.length ? f.choices.map((c) => c.label || c.value) : (f.options ?? []);
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const capitalise = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/** Differences between the live questions (`before`) and the draft (`after`), by field id. */
export function diffFields(before: FieldDef[], after: FieldDef[]): FieldChange[] {
  const out: FieldChange[] = [];
  const B = new Map(before.map((f) => [f.id, f] as const));
  const A = new Map(after.map((f) => [f.id, f] as const));

  for (const f of after) {
    if (B.has(f.id)) continue;
    const where = f.type !== 'heading' && f.audience === 'customer' ? stepNameOf(after, f.id) : '';
    out.push({
      kind: 'add',
      fieldId: f.id,
      text: `Added “${f.label}”`,
      detail:
        f.type === 'heading'
          ? 'New step'
          : fieldTypeName(f.type) + (f.audience === 'staff' ? ' · staff only' : '') + (where ? ` · in ${where}` : ''),
    });
  }
  for (const f of before) {
    if (!A.has(f.id)) {
      out.push({ kind: 'remove', fieldId: null, text: `Removed “${f.label}”`, detail: fieldTypeName(f.type) });
    }
  }
  for (const f of after) {
    const b = B.get(f.id);
    if (!b || same(b, f)) continue;
    const parts: string[] = [];
    if (b.label !== f.label) parts.push(`wording was “${b.label}”`);
    if (b.type !== f.type) parts.push(`now a ${fieldTypeName(f.type).toLowerCase()} question`);
    if (Boolean(b.required) !== Boolean(f.required)) parts.push(f.required ? 'now required' : 'now optional');
    if ((b.help ?? '') !== (f.help ?? '')) parts.push('help text changed');
    if (!same(answersOf(b), answersOf(f)) || !same(b.optionsSource, f.optionsSource)) parts.push('answers changed');
    if (!same(b.visibleWhen, f.visibleWhen)) {
      parts.push(
        hasRules(f)
          ? `now only shown when ${visibilityText(f.visibleWhen as ConditionalVisibility, after)}`
          : 'now always shown',
      );
    }
    if (b.audience !== f.audience) {
      parts.push(f.audience === 'staff' ? 'now answered by staff' : 'now answered by the applicant');
    }
    if (!same(b.companyLookup, f.companyLookup)) parts.push('Companies House look-up changed');
    if (Boolean(b.answerFirst) !== Boolean(f.answerFirst)) {
      parts.push(f.answerFirst ? 'now answered before the rest of its step' : 'no longer holds back the rest of its step');
    }
    if (!same(b.address?.countryFrom, f.address?.countryFrom)) parts.push('where its country starts from changed');
    if ((b.address?.postcodeLookup === false) !== (f.address?.postcodeLookup === false)) {
      parts.push(f.address?.postcodeLookup === false ? 'no longer finds addresses by postcode' : 'now finds addresses by postcode');
    }
    if (!parts.length) parts.push('other settings changed');
    out.push({ kind: 'edit', fieldId: f.id, text: `Changed “${f.label}”`, detail: capitalise(parts.join('; ')) });
  }
  const orderBefore = before.filter((f) => A.has(f.id)).map((f) => f.id);
  const orderAfter = after.filter((f) => B.has(f.id)).map((f) => f.id);
  if (!same(orderBefore, orderAfter)) {
    out.push({
      kind: 'move',
      fieldId: null,
      text: 'Questions reordered',
      detail: 'Some questions are in a different order or step',
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Pre-publish checks
// ---------------------------------------------------------------------------

export interface FormProblem {
  /** The question to jump to; null when the fix is elsewhere. */
  fieldId: string | null;
  /** Set when the fix is on the Form settings tab. */
  tab?: 'settings';
  text: string;
  fix: string;
}

export interface FormProblems {
  /** Must fix: these block publishing. */
  errors: FormProblem[];
  /** Worth a look: don't block. */
  warnings: FormProblem[];
}

const MAPPED_FIELD_SOURCES = new Set(['field', 'derived', 'contact', 'lookup']);

function mappingFieldId(m: IqMappingEntry): string | undefined {
  const s = m.source as { type: string; fieldId?: string };
  return MAPPED_FIELD_SOURCES.has(s.type) ? s.fieldId : undefined;
}

/** What would break, or look wrong, if this draft were published. */
export function formProblems(fields: FieldDef[], settings: FormSettings = {}): FormProblems {
  const ids = new Set(fields.map((f) => f.id));
  const errors: FormProblem[] = [];
  const warnings: FormProblem[] = [];

  for (const f of fields) {
    let broken = false;
    walkRules(f.visibleWhen as ConditionalVisibility | undefined, (r) => {
      if (!ids.has(String(r.fieldId).split('.')[0]!)) broken = true;
    });
    if (broken) {
      errors.push({ fieldId: f.id, text: `“${f.label}” has a show/hide rule that uses a question you deleted.`, fix: 'Fix the rule' });
    }
    if (f.defaultWhen && !ids.has(String(f.defaultWhen.fieldId).split('.')[0]!)) {
      errors.push({ fieldId: f.id, text: `The starting answer for “${f.label}” depends on a question you deleted.`, fix: 'Fix it' });
    }
    if (subFieldSources(f).some((from) => !ids.has(from))) {
      errors.push({ fieldId: f.id, text: `A line in “${f.label}” starts as the answer to a question you deleted.`, fix: 'Fix it' });
    }
    if (countryLinks(f).some((from) => !ids.has(from))) {
      errors.push({ fieldId: f.id, text: `The country for “${f.label}” comes from a question you deleted.`, fix: 'Fix it' });
    }
    if (!String(f.label ?? '').trim()) {
      errors.push({ fieldId: f.id, text: 'A question has no wording.', fix: 'Add wording' });
    }
    const isBoolCheckbox = f.type === 'checkbox' && !answersOf(f).length;
    if (CHOICE_TYPES.includes(f.type) && !isBoolCheckbox && !f.optionsSource && !answersOf(f).length) {
      errors.push({ fieldId: f.id, text: `“${f.label}” has no answers to choose from.`, fix: 'Add answers' });
    }
    if (f.optionsSource && !f.optionsSource.key) {
      errors.push({ fieldId: f.id, text: `“${f.label}” is set to use an iQ list, but no list is chosen.`, fix: 'Choose a list' });
    }
    if (f.type === 'company_number') {
      const cl = f.companyLookup ?? {};
      if (!cl.nameFieldId || !ids.has(cl.nameFieldId)) {
        warnings.push({ fieldId: f.id, text: `“${f.label}” doesn’t fill in a company name question.`, fix: 'Choose one' });
      }
      if (!cl.addressFieldId || !ids.has(cl.addressFieldId)) {
        warnings.push({ fieldId: f.id, text: `“${f.label}” doesn’t fill in a registered address question.`, fix: 'Choose one' });
      }
    }
    if (f.type === 'contact' || f.type === 'repeater') {
      for (const s of f.subFields ?? []) {
        if (s.iqField && !looksLikeIqField(s.iqField)) {
          warnings.push({
            fieldId: f.id,
            text: `“${s.label}” in “${f.label}” goes to iQ as “${s.iqField}”, which doesn’t look like an iQ field.`,
            fix: 'Check it',
          });
        } else if (!s.iqField && !suggestContactField(s)) {
          warnings.push({ fieldId: f.id, text: `“${s.label}” in “${f.label}” isn’t sent to iQ.`, fix: 'Choose its iQ field' });
        }
      }
    }
    if (f.type === 'consent' && !String(f.content ?? '').trim()) {
      warnings.push({ fieldId: f.id, text: `“${f.label}” has no terms text for applicants to read.`, fix: 'Add the terms' });
    }
  }

  // Applicant steps with nothing in them show an empty page.
  let step: FieldDef | null = null;
  let count = 0;
  const flush = () => {
    if (step && count === 0) {
      warnings.push({ fieldId: step.id, text: `The step “${step.label}” has no questions, so applicants will see an empty page.`, fix: 'Go to step' });
    }
  };
  for (const f of fields.filter((x) => x.audience === 'customer')) {
    if (f.type === 'heading') {
      flush();
      step = f;
      count = 0;
    } else {
      count++;
    }
  }
  flush();

  for (const m of [...(settings.iqMappings ?? []), ...(settings.iqApprovalMappings ?? [])]) {
    const fid = mappingFieldId(m);
    if (fid && !ids.has(fid)) {
      errors.push({
        fieldId: null,
        tab: 'settings',
        text: `The iQ field ${m.target} takes its value from a question you deleted.`,
        fix: 'Open form settings',
      });
    }
  }
  return { errors, warnings };
}

/** The country questions an address (or a contact's addresses) pre-fill from. */
function countryLinks(f: FieldDef): string[] {
  const own = f.type === 'address' ? [f.address?.countryFrom] : [];
  const subs = (f.subFields ?? []).filter((s) => s.type === 'address').map((s) => s.address?.countryFrom);
  return [...own, ...subs].filter((x): x is string => Boolean(x));
}

/** The questions a contact's lines start as (e.g. Company name). */
function subFieldSources(f: FieldDef): string[] {
  return (f.subFields ?? []).map((s) => s.defaultFrom).filter((x): x is string => Boolean(x));
}

/**
 * What else uses a question — show/hide rules, starting answers, company
 * look-up targets, iQ mappings — so deleting it can warn first. One line each.
 */
export function fieldReferences(fields: FieldDef[], settings: FormSettings, fieldId: string): string[] {
  const out: string[] = [];
  const uses = (path: string | undefined) => String(path ?? '').split('.')[0] === fieldId;
  for (const f of fields) {
    if (f.id === fieldId) continue;
    let ruled = false;
    walkRules(f.visibleWhen as ConditionalVisibility | undefined, (r) => {
      if (uses(r.fieldId)) ruled = true;
    });
    if (ruled) out.push(`“${f.label}” is only shown depending on it.`);
    if (f.defaultWhen && uses(f.defaultWhen.fieldId)) out.push(`“${f.label}” takes its starting answer from it.`);
    if (countryLinks(f).includes(fieldId)) out.push(`“${f.label}” takes its country from it.`);
    if (subFieldSources(f).includes(fieldId)) out.push(`Each entry in “${f.label}” starts with its answer.`);
    if (f.companyLookup?.nameFieldId === fieldId || f.companyLookup?.addressFieldId === fieldId) {
      out.push(`“${f.label}” fills it in from Companies House.`);
    }
  }
  for (const m of [...(settings.iqMappings ?? []), ...(settings.iqApprovalMappings ?? [])]) {
    if (mappingFieldId(m) === fieldId) out.push(`It is sent to iQ as ${m.target}.`);
  }
  return out;
}

